import Banner from "@/components/Banner";
import Button from "@/components/Button";
import DetailsCard from "@/components/DetailsCard";
import Dialog from "@/components/Dialog";
import MetricGrid from "@/components/MetricGrid";
import SectionHeading from "@/components/SectionHeading";
import Tooltip from "@/components/Tooltip";
import { PersonObjectHeading } from "@/features/person/components/PersonObjectHeading";
import { PERSON_CONTACT_CHROME_QUERY, PERSON_DETAILS_HEADER_QUERY } from "@/features/person/queries";
import { DOMAIN, PERMISSION, useHasCapabilityBool, useRequireCapability } from "@/hooks/useCapabilities";
import usePageTitle from "@/hooks/usePageTitle";
import { useReturnTo } from "@/hooks/useSearchParams";
import FocusedPageLayout from "@/layouts/FocusedPageLayout";
import { getTime } from "@/lib/dateHelpers";
import { renderName } from "@/lib/personHelpers";
import { Path, useParams } from "@/router";
import { useMutation, useQuery } from "@apollo/client";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  GET_PAYOUT_PROFILES_QUERY,
  MARK_PAYOUT_PROFILE_INACTIVE_MUTATION,
  PayoutProfile,
  PayoutProfileAllowedActionEnum,
  REMOVE_PAYOUT_PROFILE_MUTATION,
  SYNC_WORKDAY_SUPPLIER_MUTATION,
  TaxIdType
} from "../queries";
import { getStatusData, isPersonal, llcClassificationDisplay, taxClassificationDisplay } from "../utils";
import BankConnectionProviderIcon from "../_components/BankConnectionProviderIcon";
import TokenizedAccountNumberHint from "../_components/TokenizedAccountNumberHint";
import { bankConnectionProviderLabel, isLinkedBankConnectionProvider } from "../bankConnectionProvider";
import { capitalize } from "lodash";
import { showSnackbar, showErrorSnackbar } from "@/lib/snackbarUtils";

function DetailsColumn({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col gap-y-10">{children}</div>;
}

interface AddressDisplayProps {
  address: string;
  address2?: string | null;
  city: string;
  state: string;
  postalCode: string;
  country?: string | null;
}

function AddressDisplay({ address, address2, city, state, postalCode, country }: AddressDisplayProps) {
  return (
    <div className="flex flex-col">
      <span>
        {address}
        {address2 ? `, ${address2}` : ""}
      </span>
      <span>
        {city}, {state} {postalCode}
        {country ? `, ${country}` : ""}
      </span>
    </div>
  );
}

const taxIdDisplay = (format: "ssn" | "ein" | "other", taxId?: TaxIdType) => {
  if (!taxId) return null;

  const maskedTaxId = (initialNumber?: string) => {
    let number = initialNumber ? initialNumber.replace(/-/g, "") : "";
    number = (number || "").padStart(9, "•");
    if (format === "ein") {
      return `${number.slice(0, 2)}-${number.slice(2)}`;
    } else if (format === "other") {
      return number;
    } else if (format === "ssn") {
      return `${number.slice(0, 3)}-${number.slice(3, 5)}-${number.slice(5)}`;
    }
  };

  const number = taxId.full || taxId.last4;

  return number ? (
    <Tooltip
      variant="plain"
      color="black"
      supportingText={maskedTaxId(number)}
      menuCorner="end-start"
      anchorCorner="start-start"
    >
      {maskedTaxId()}
    </Tooltip>
  ) : (
    maskedTaxId()
  );
};

const formatDate = (date: string) => {
  return getTime(date, DateTime.DATE_MED);
};

const SYNCABLE_STATUSES = ["failed_to_sync", "draft"];

export default function PayoutProfileView() {
  const { pgaId, payoutProfileId } = useParams("/people/:pgaId/billing/payout-profiles/:payoutProfileId/view");
  useRequireCapability({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPersonBilling });
  const canViewPersonBilling = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonBilling
  });
  const location = useLocation();
  const navigate = useNavigate();
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [showMarkInactiveDialog, setShowMarkInactiveDialog] = useState(false);

  const canViewPersonContact = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonContact
  });

  const { data: personData } = useQuery(PERSON_DETAILS_HEADER_QUERY, {
    variables: { pgaId }
  });

  const { data: contactChromeData } = useQuery(PERSON_CONTACT_CHROME_QUERY, {
    variables: { pgaId },
    skip: !pgaId || !canViewPersonContact,
    fetchPolicy: "network-only"
  });
  const contactPerson = contactChromeData?.person;

  const { data: payoutProfilesData, refetch } = useQuery(GET_PAYOUT_PROFILES_QUERY, {
    variables: { pgaId, payoutProfileId },
    skip: !pgaId || !payoutProfileId || !canViewPersonBilling
  });

  const [syncWorkdaySupplier] = useMutation(SYNC_WORKDAY_SUPPLIER_MUTATION);
  const [removePayoutProfile, { loading: removingProfile }] = useMutation(REMOVE_PAYOUT_PROFILE_MUTATION);
  const [markPayoutProfileInactive, { loading: markingInactive }] = useMutation(MARK_PAYOUT_PROFILE_INACTIVE_MUTATION);

  const profile = payoutProfilesData?.payoutProfiles?.nodes?.[0] as PayoutProfile | undefined;
  const profileStatus = profile?.status;
  const allowedActions = profile?.allowedActions || [];
  const canDeactivateProfile = allowedActions.includes(PayoutProfileAllowedActionEnum.DEACTIVATE_PAYOUT_PROFILE);
  const canMarkInactive = allowedActions.includes(PayoutProfileAllowedActionEnum.MARK_INACTIVE_PAYOUT_PROFILE);

  useEffect(() => {
    if (profileStatus !== "processing") return;
    const intervalId = setInterval(refetch, 5000);
    return () => clearInterval(intervalId);
  }, [profileStatus, refetch]);

  const handleSyncWorkday = async () => {
    if (!payoutProfileId) return;
    try {
      const { data: mutationData } = await syncWorkdaySupplier({ variables: { payoutProfileId } });

      if (mutationData?.syncWorkdaySupplier?.success) {
        refetch();
        showSnackbar(mutationData.syncWorkdaySupplier.message || "Workday supplier sync scheduled");
      } else {
        showErrorSnackbar(mutationData?.syncWorkdaySupplier?.message || "Failed to sync to Workday");
      }
    } catch {
      showErrorSnackbar("Workday sync not scheduled", { subtitle: "Please try again later." });
    }
  };

  const personName = personData?.person
    ? renderName(
        personData.person.displayName,
        personData.person.informalName,
        personData.person.preferredLastName,
        personData.person.suffixName,
        personData.person.middleName,
        personData.person.firstName
      )
    : "";
  usePageTitle(location.pathname, personName ? `People | ${personName}` : "People");

  const returnTo = useReturnTo();
  const backLink = {
    label: "Back",
    to: returnTo || ("/people/:pgaId/billing/payout-profiles" as Path),
    params: { pgaId }
  } as any;

  const handleDeactivatePayoutProfile = async () => {
    if (!payoutProfileId) return;
    try {
      const { data: mutationData } = await removePayoutProfile({ variables: { payoutProfileId } });

      if (mutationData?.removePayoutProfile?.success) {
        showSnackbar(mutationData.removePayoutProfile.message || "Payout profile deactivated");
        navigate(backLink.to.replace(":pgaId", pgaId));
      } else {
        showErrorSnackbar(mutationData?.removePayoutProfile?.message || "Failed to deactivate payout profile");
        setShowDeactivateDialog(false);
      }
    } catch {
      showErrorSnackbar("Payout profile not deactivated", { subtitle: "Please try again later." });
      setShowDeactivateDialog(false);
    }
  };

  const handleMarkPayoutProfileInactive = async () => {
    if (!payoutProfileId) return;
    try {
      const { data: mutationData } = await markPayoutProfileInactive({ variables: { payoutProfileId } });

      if (mutationData?.markPayoutProfileInactive?.success) {
        showSnackbar(mutationData.markPayoutProfileInactive.message || "Payout profile marked inactive");
        navigate(backLink.to.replace(":pgaId", pgaId));
      } else {
        showErrorSnackbar(mutationData?.markPayoutProfileInactive?.message || "Failed to mark payout profile inactive");
        setShowMarkInactiveDialog(false);
      }
    } catch {
      showErrorSnackbar("Payout profile not marked inactive", { subtitle: "Please try again later." });
      setShowMarkInactiveDialog(false);
    }
  };

  const extendedActions = [] as any;
  const actions = profile ? (
    <>
      {SYNCABLE_STATUSES.includes(profile.status) && (
        <Button variant="outlined" icon="sync" onClick={handleSyncWorkday} testId="sync-to-workday">
          Sync to Workday
        </Button>
      )}
      {canMarkInactive && (
        <Button
          variant="outlined"
          theme="error"
          icon="block"
          onClick={() => setShowMarkInactiveDialog(true)}
          disabled={markingInactive}
          testId="mark-inactive-payout-profile"
        >
          Mark Inactive
        </Button>
      )}
      {canDeactivateProfile && (
        <Button
          variant="outlined"
          theme="error"
          icon="person_off"
          onClick={() => setShowDeactivateDialog(true)}
          disabled={removingProfile}
          testId="deactivate-payout-profile"
        >
          Deactivate
        </Button>
      )}
    </>
  ) : null;

  if (!profile) {
    const actions = null;
    return (
      <FocusedPageLayout backLink={backLink} actions={actions} extendedActions={extendedActions} stickyActions={true}>
        <PersonObjectHeading pgaId={pgaId} hideActions disableScrollAdjustment />
        <div className="p-8 text-sys-error">Payout profile not found.</div>
      </FocusedPageLayout>
    );
  }

  const {
    name,
    address,
    address2,
    city,
    stateDisplay,
    postalCode,
    taxClassification,
    llcClassification,
    exemptPayeeCode,
    taxFormType,
    defaultPayoutProfile,
    businessName,
    fatcaExemptionCode,
    signature,
    signatureDate,
    backupWithholding,
    bankName,
    createdAt,
    updatedAt,
    bankAccountDisplayName,
    bankAccountMaskedNumber,
    bankConnectionProvider,
    bankReauthorizationRequired,
    stripeFcExpectedExpiryDate,
    stripeFcAccountNumberStatus,
    stripeFcAccountNumberIdentifierType,
    ssn,
    ein,
    workdayUserError,
    workdayServerException,
    workdaySupplierId,
    countryDisplay,
    countryOfCitizenshipDisplay,
    mailingAddress,
    mailingAddress2,
    mailingCity,
    mailingStateDisplay,
    mailingPostalCode,
    mailingCountryDisplay,
    foreignTaxIdRequired,
    w8ReferenceNumber,
    dateOfBirth,
    treatyCountryDisplay,
    specialRatesArticle,
    specialRatesPercent,
    specialRatesIncomeType,
    specialRatesConditions,
    foreignTaxId,
    paperless1099
  } = profile;

  const statusData = getStatusData(profile);
  const showErrorBanner = !!workdayUserError || !!workdayServerException;
  const bankConnectionLabel = bankConnectionProviderLabel(bankConnectionProvider);
  const showBankConnectionProvider = bankConnectionProvider && isLinkedBankConnectionProvider(bankConnectionProvider);

  return (
    <FocusedPageLayout backLink={backLink} actions={actions} extendedActions={extendedActions} stickyActions={true}>
      <PersonObjectHeading pgaId={pgaId} hideActions disableScrollAdjustment />
      {bankReauthorizationRequired && (
        <div className="mt-6">
          <Banner
            variant="error"
            description="This member's bank has ended authorization for their secure payout bank connection. Payouts are paused until the member reconnects via Contestant Payouts on my.pga.com. Staff cannot re-link Financial Connections. This is required by their bank, not by the PGA."
            inline={true}
            closable={false}
            testId="bank-reauth-required-banner"
          />
        </div>
      )}
      {showErrorBanner && (
        <div className="mt-6">
          <Banner
            variant="error"
            description={[
              "There is an error with this payout profile profile. Please contact support for assistance.",
              workdayUserError ? `User Error: ${workdayUserError}` : null,
              workdayServerException ? `Server Error: ${workdayServerException}` : null
            ]
              .filter(Boolean)
              .join("\n")}
            inline={true}
            closable={false}
            testId="profile-error-banner"
          />
        </div>
      )}
      <MetricGrid
        metrics={[
          {
            label: "Supplier ID",
            value: workdaySupplierId,
            iconName: "badge"
          },
          {
            label: "Workday Status",
            value: statusData.label,
            iconName: "manage_accounts",
            variant: statusData.variant,
            onClick: !!profile.workdaySupplierUrl
              ? () => window.open(profile.workdaySupplierUrl!, "_blank", "noopener,noreferrer")
              : undefined,
            tooltip: !profile.workdaySupplierId
              ? {
                  supportingText: "Account holder has not completed/submitted their payout profile."
                }
              : undefined
          }
        ]}
        layout="expanded"
      />
      <SectionHeading
        heading="PGA Payouts"
        body="View tax details and PGA payout profile details set up to receive payments from a PGA or PGA Section entity. This is required for payout of tournament winnings and other PGA-related payments."
      />
      <div className="flex flex-col md:flex-row gap-x-10" data-testid="payout-profile-details">
        <div id="payout-profile-column-1" className="basis-1/3 pb-10">
          <DetailsColumn>
            <DetailsCard
              heading={`${isPersonal(profile) ? "Personal" : "Business"} Payout Profile`}
              options={[
                {
                  iconName: "account_balance",
                  overline: "Institution",
                  headline: bankName || "--",
                  supportingText:
                    showBankConnectionProvider && bankConnectionProvider && bankConnectionLabel ? (
                      <span className="inline-flex items-center gap-2">
                        <BankConnectionProviderIcon provider={bankConnectionProvider} />
                        {bankConnectionLabel}
                      </span>
                    ) : undefined
                },
                {
                  iconName: "credit_card",
                  overline: "Account",
                  headline: (
                    <TokenizedAccountNumberHint
                      maskedNumber={bankAccountMaskedNumber}
                      identifierType={stripeFcAccountNumberIdentifierType}
                      emptyFallback="--"
                    />
                  ),
                  supportingText: bankAccountDisplayName
                },
                ...(stripeFcExpectedExpiryDate
                  ? [
                      {
                        iconName: "schedule" as const,
                        overline: "Bank Connection Expiry",
                        headline: formatDate(stripeFcExpectedExpiryDate),
                        supportingText: stripeFcAccountNumberStatus
                          ? `Status: ${stripeFcAccountNumberStatus}`
                          : undefined
                      }
                    ]
                  : []),
                {
                  iconName: "description",
                  overline: "Terms and Conditions",
                  headline: "Agreed",
                  supportingText: createdAt ? formatDate(createdAt) : "--"
                },
                {
                  iconName: "account_circle",
                  overline: "Default Payout Profile",
                  headline: defaultPayoutProfile ? "Yes" : "No"
                },
                {
                  iconName: "update",
                  overline: "Last Updated",
                  headline: updatedAt ? formatDate(updatedAt) : "--"
                }
              ]}
            />
          </DetailsColumn>
          {canViewPersonContact && (contactPerson?.email || (contactPerson?.phoneNumbers?.length ?? 0) > 0) && (
            <div className="mt-8">
              <DetailsColumn>
                <DetailsCard
                  heading="Contact Information"
                  options={[
                    ...(contactPerson?.email
                      ? [
                          {
                            iconName: "email" as const,
                            overline: "Email",
                            headline: contactPerson.email
                          }
                        ]
                      : []),
                    ...(contactPerson?.phoneNumbers?.map((phone: { phoneFormatted: string; phoneType: string }) => ({
                      iconName: "phone" as const,
                      overline: capitalize(phone.phoneType || "Phone"),
                      headline: phone.phoneFormatted
                    })) ?? [])
                  ]}
                />
              </DetailsColumn>
            </div>
          )}
        </div>
        <div id="payout-profile-column-2" className="basis-2/3">
          <DetailsColumn>
            {taxFormType === "w9" && (
              <DetailsCard
                heading={`${isPersonal(profile) ? "Personal" : "Business"} W-9 Details`}
                options={[
                  {
                    iconName: "person",
                    overline: "Legal Name (Individual or Business Owner)",
                    headline: name || "--"
                  },
                  {
                    iconName: "store",
                    overline: "Business Name",
                    headline: businessName || "--"
                  },
                  {
                    iconName: "category",
                    overline: "Federal Tax Classification",
                    headline: taxClassificationDisplay(taxClassification || "--")
                  },
                  ...(llcClassification
                    ? [
                        {
                          iconName: "category" as const,
                          overline: "LLC Tax Classification",
                          headline: llcClassificationDisplay(llcClassification)
                        }
                      ]
                    : []),
                  {
                    iconName: "tag",
                    overline: "Exempt Payee Code",
                    headline: exemptPayeeCode || "--"
                  },
                  {
                    iconName: "tag",
                    overline: "Exemption from FATCA Reporting Code",
                    headline: fatcaExemptionCode || "--"
                  },
                  {
                    iconName: "badge",
                    overline: "Taxpayer Identification Number",
                    headline: (
                      <div className="flex flex-col">
                        <span>{taxIdDisplay("ssn", ssn) || taxIdDisplay("ein", ein) || "--"}</span>
                        <span>{ssn ? "SSN" : ein ? "EIN" : ""}</span>
                      </div>
                    )
                  },
                  {
                    iconName: "markunread_mailbox",
                    overline: "Tax Documents Mailing Address",
                    headline: (
                      <AddressDisplay
                        address={address}
                        address2={address2}
                        city={city}
                        state={stateDisplay}
                        postalCode={postalCode}
                      />
                    )
                  },
                  {
                    iconName: "credit_card",
                    overline: "Backup Withholding",
                    headline: backupWithholding == null ? "--" : backupWithholding ? "Yes" : "No"
                  },
                  ...(taxClassification === "individual_sole_proprietor"
                    ? [
                        {
                          iconName: "description" as const,
                          overline: "1099 Delivery",
                          headline: paperless1099 ? "Email" : "Paper mail"
                        }
                      ]
                    : []),
                  {
                    iconName: "verified_user",
                    overline: "Certification",
                    headline: signature ? `Signed by ${signature}` : "--",
                    supportingText: signatureDate ? formatDate(signatureDate) : ""
                  }
                ]}
              />
            )}
            {taxFormType === "w8" && (
              <DetailsCard
                heading="Personal W-8 Details (Foreign)"
                options={[
                  {
                    iconName: "person",
                    overline: "Legal Name",
                    headline: name || "--"
                  },
                  {
                    iconName: "public",
                    overline: "Country of Citizenship",
                    headline: countryOfCitizenshipDisplay || "--"
                  },
                  {
                    iconName: "home",
                    overline: "Permanent Residence Address",
                    headline: (
                      <AddressDisplay
                        address={address}
                        address2={address2}
                        city={city}
                        state={stateDisplay}
                        postalCode={postalCode}
                        country={countryDisplay}
                      />
                    )
                  },
                  ...(mailingAddress
                    ? [
                        {
                          iconName: "markunread_mailbox" as const,
                          overline: "Tax Documents Mailing Address",
                          headline: (
                            <AddressDisplay
                              address={mailingAddress}
                              address2={mailingAddress2}
                              city={mailingCity!}
                              state={mailingStateDisplay!}
                              postalCode={mailingPostalCode!}
                              country={mailingCountryDisplay}
                            />
                          )
                        }
                      ]
                    : []),
                  {
                    iconName: "badge",
                    overline: "Taxpayer Identification Number (SSN or ITIN)",
                    headline: taxIdDisplay("ssn", ssn) || "--"
                  },
                  {
                    iconName: "badge",
                    overline: "Foreign Tax Identifying Number (FTIN)",
                    headline: taxIdDisplay("other", foreignTaxId) || "--"
                  },
                  {
                    iconName: "badge",
                    overline: "FTIN is not legally required",
                    headline: !foreignTaxIdRequired ? "Yes" : "No"
                  },
                  {
                    iconName: "numbers",
                    overline: "Reference Number",
                    headline: w8ReferenceNumber || "--"
                  },
                  ...(dateOfBirth
                    ? [
                        {
                          iconName: "cake" as const,
                          overline: "Date of Birth",
                          headline: formatDate(dateOfBirth)
                        }
                      ]
                    : []),
                  {
                    iconName: "public",
                    overline: "Treaty Benefits",
                    headline: treatyCountryDisplay ? `Yes - ${treatyCountryDisplay}` : "No"
                  },
                  ...(specialRatesArticle
                    ? [
                        {
                          iconName: "description" as const,
                          overline: "Special Rates Article",
                          headline: specialRatesArticle
                        }
                      ]
                    : []),
                  ...(specialRatesPercent
                    ? [
                        {
                          iconName: "percent" as const,
                          overline: "Special Rates Percent",
                          headline: specialRatesPercent
                        }
                      ]
                    : []),
                  ...(specialRatesIncomeType
                    ? [
                        {
                          iconName: "attach_money" as const,
                          overline: "Type of Income",
                          headline: specialRatesIncomeType
                        }
                      ]
                    : []),
                  ...(specialRatesConditions
                    ? [
                        {
                          iconName: "info" as const,
                          overline: "Special Rates Conditions",
                          headline: specialRatesConditions
                        }
                      ]
                    : []),

                  {
                    iconName: "verified_user",
                    overline: "Certification",
                    headline: signature ? `Signed by ${signature}` : "--",
                    supportingText: signatureDate ? formatDate(signatureDate) : ""
                  }
                ]}
              />
            )}
          </DetailsColumn>
        </div>
      </div>
      <Dialog
        open={showMarkInactiveDialog}
        onClosed={() => setShowMarkInactiveDialog(false)}
        headline="Mark Payout Profile Inactive"
        className="w-[490px] p-6 pr-2"
        actions={
          <>
            <Button variant="text" onClick={() => setShowMarkInactiveDialog(false)} disabled={markingInactive}>
              Cancel
            </Button>
            <Button
              variant="filled"
              theme="error"
              icon={markingInactive ? "progress_activity" : "block"}
              onClick={handleMarkPayoutProfileInactive}
              disabled={markingInactive}
              testId="confirm-mark-inactive-payout-profile"
            >
              Mark Inactive
            </Button>
          </>
        }
      >
        <p>Are you sure you want to mark this payout profile inactive?</p>
      </Dialog>
      <Dialog
        open={showDeactivateDialog}
        onClosed={() => setShowDeactivateDialog(false)}
        headline="Deactivate Payout Profile"
        className="w-[490px] p-6 pr-2"
        actions={
          <>
            <Button variant="text" onClick={() => setShowDeactivateDialog(false)} disabled={removingProfile}>
              Cancel
            </Button>
            <Button
              variant="filled"
              theme="error"
              icon={removingProfile ? "progress_activity" : "person_off"}
              onClick={handleDeactivatePayoutProfile}
              disabled={removingProfile}
              testId="confirm-deactivate-payout-profile"
            >
              Deactivate
            </Button>
          </>
        }
      >
        <p>Are you sure you want to deactivate this payout profile?</p>
      </Dialog>
    </FocusedPageLayout>
  );
}
